import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { isAuthFailureError, isServiceUnavailableError } from '../services/api';
import { clearApiTokenDismissSession } from '../utils/notificationsSession';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  /** Set when profile could not be refreshed but session is kept (deploy / restart). */
  const [connectionIssue, setConnectionIssue] = useState(null);

  const dismissConnectionIssue = useCallback(() => {
    setConnectionIssue(null);
  }, []);

  const checkAuth = useCallback(async () => {
    const token = api.getToken();
    if (token) {
      try {
        const response = await api.getProfile();
        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
          setConnectionIssue(null);
          const impersonating =
            typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem('impersonation_original_token');
          if (impersonating) {
            api.setUser(response.data, false);
          } else {
            api.setUser(response.data, !!localStorage.getItem('token'));
          }
        }
      } catch (error) {
        if (isAuthFailureError(error)) {
          api.logout();
          setUser(null);
          setIsAuthenticated(false);
          setConnectionIssue(null);
        } else {
          const cached = api.getUser();
          if (cached) {
            setUser(cached);
            setIsAuthenticated(true);
            setConnectionIssue(isServiceUnavailableError(error) ? 'maintenance' : null);
          } else {
            setUser(null);
            setIsAuthenticated(false);
            setConnectionIssue(null);
          }
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const response = await api.login({ email, password }, rememberMe);
    if (response.data && response.data.user) {
      clearApiTokenDismissSession();
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response;
  };

  const register = async (userData) => {
    return await api.register(userData);
  };

  const logout = () => {
    clearApiTokenDismissSession();
    api.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const verifyEmail = async (token) => {
    return await api.verifyEmail(token);
  };

  const resendVerification = async (email) => {
    return await api.resendVerification(email);
  };

  const requestPasswordReset = async (email) => {
    return await api.requestPasswordReset(email);
  };

  const confirmPasswordReset = async (token, newPassword, newPasswordConfirm) => {
    return await api.confirmPasswordReset(token, newPassword, newPasswordConfirm);
  };

  const changePassword = async (oldPassword, newPassword, newPasswordConfirm) => {
    return await api.changePassword(oldPassword, newPassword, newPasswordConfirm);
  };

  const updateProfile = async (userData) => {
    const response = await api.updateProfile(userData);
    if (response.data) {
      setUser(response.data);
    }
    return response;
  };

  const refreshProfile = async () => {
    try {
      const response = await api.getProfile();
      if (response.data) {
        setUser(response.data);
        setConnectionIssue(null);
        const impersonating =
          typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('impersonation_original_token');
        if (impersonating) {
          api.setUser(response.data, false);
        } else {
          api.setUser(response.data, !!localStorage.getItem('token'));
        }
      }
      return response;
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      throw error;
    }
  };

  const impersonate = (token, newUser) => {
    clearApiTokenDismissSession();
    const currentToken = api.getToken();
    if (currentToken && user) {
      sessionStorage.setItem('impersonation_original_token', currentToken);
      sessionStorage.setItem('impersonation_original_user', JSON.stringify(user));
      sessionStorage.setItem('impersonation_original_remember', localStorage.getItem('token') ? '1' : '0');
    }
    api.setToken(token, false);
    api.setUser(newUser, false);
    setUser(newUser);
    setIsAuthenticated(true);
    // Перезагрузка страницы, чтобы заново подгрузить заказы, магазины, биллинг под выбранным клиентом
    window.location.reload();
  };

  const exitImpersonation = () => {
    const origToken = sessionStorage.getItem('impersonation_original_token');
    const origUser = sessionStorage.getItem('impersonation_original_user');
    const origRemember = sessionStorage.getItem('impersonation_original_remember') === '1';
    sessionStorage.removeItem('impersonation_original_token');
    sessionStorage.removeItem('impersonation_original_user');
    sessionStorage.removeItem('impersonation_original_remember');
    if (origToken) {
      api.setToken(origToken, origRemember);
      api.setUser(origUser ? JSON.parse(origUser) : null, origRemember);
      // После выхода из impersonation токен клиента оставался в sessionStorage; при «Запомнить меня»
      // админ снова только в localStorage — убираем дубликаты из session.
      if (origRemember) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      }
      setUser(origUser ? JSON.parse(origUser) : null);
      setIsAuthenticated(!!origUser);
    } else {
      api.removeToken();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const isImpersonating = () => {
    return typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('impersonation_original_token');
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    connectionIssue,
    dismissConnectionIssue,
    login,
    register,
    logout,
    verifyEmail,
    resendVerification,
    requestPasswordReset,
    confirmPasswordReset,
    changePassword,
    updateProfile,
    refreshProfile,
    impersonate,
    exitImpersonation,
    isImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
